import Storage from './storage';
import StorageItem from './storage-item';
import GarbageCollector from './garbage-collector';

/* This modules will hold the storages, one per collection
 It will also hold a reference to the garbage collector.
 */
function StorageManager(config) {
    const storages = {};
    let repositorySize = 0;
    const maxRepositorySize = config.maxRepositorySize;
    const garbageCollector = new GarbageCollector();

    this.addStorage = function (collection, cfg) {
        storages[collection] = new Storage(collection, cfg);
    };

    this.getById = function (collection, identifier) {
        const storage = getStorage(collection);
        const result = storage.get(identifier);
        return result.error
            ? Promise.reject(result.error)
            : Promise.resolve(result.item.getObject());
    };

    this.save = function (collection, identifier, object) {
        const storage = getStorage(collection);
        const item = new StorageItem(object);
        const newItemSize = item.getSize();

        const error = verifyStorageSize(storage, newItemSize) || verifyRepositorySize(newItemSize);
        let promise;
        if (error) {
            promise = Promise.reject(error);
        } else {
            const oldSize = storage.storageSize;
            storage.save(identifier, item);
            repositorySize += storage.storageSize - oldSize;
            promise = Promise.resolve(object);
        }
        return promise;
    };

    this.remove = function (collection, identifier) {
        const storage = getStorage(collection);
        const oldSize = storage.storageSize;
        const result = storage.remove(identifier);
        repositorySize += storage.storageSize - oldSize;
        return result.error
            ? Promise.reject(result.error)
            : Promise.resolve(result.item.getObject());
    };

    this.instrumentation = function () {
        return {
            __name__: 'Storage Manager',
            __this__: true,
            repositorySize: repositorySize,
            storages: storages,
            garbageCollector: garbageCollector,
            memorySummary: {
                instrumentation: function () {
                    const summary = {
                        __name__: 'Memory Summary',
                    };
                    Object.keys(storages).forEach(function (key) {
                        const storageSize = storages[key].storageSize;
                        let formatedSize;
                        if (storageSize > 1e9) {
                            formatedSize = `${Math.round(storageSize / 1e9)} G`;
                        } else if (storageSize > 1e6) {
                            formatedSize = `${Math.round(storageSize / 1e6)} M`;
                        } else if (storageSize > 1e3) {
                            formatedSize = `${Math.round(storageSize / 1e3)} K`;
                        }
                        let propertyName = `Garbage collect ${key} ${storageSize}`;
                        if (formatedSize) {
                            propertyName += ` (${formatedSize})`;
                        }

                        summary[propertyName] = function () {
                            const result = garbageCollector.gc(storages[key].getRemovables());
                            repositorySize -= result.removedSize;
                        };
                    });
                    return summary;
                },
            },
            garbageCollect: function () {
                let list = [];
                Object.keys(storages).forEach(function (key) {
                    list = list.concat(storages[key].getRemovables());
                });
                const result = garbageCollector.gc(list);
                repositorySize -= result.removedSize;
            },
        };
    };

    function verifyRepositorySize(newItemSize) {
        let sizeLeft = maxRepositorySize - repositorySize;
        if (maxRepositorySize > 0 && newItemSize > sizeLeft) {
            let allItems = [];
            Object.keys(storages).forEach(function (key) {
                allItems = allItems.concat(storages[key].getRemovables());
            });

            const result = garbageCollector.gc(allItems);
            repositorySize -= result.removedSize;
            if (result.error) {
                return result.error;
            }

            sizeLeft = maxRepositorySize - repositorySize;
            if (newItemSize > sizeLeft) {
                return new Error(`Could not free enough space (${newItemSize})`);
            }
        }
    }

    function verifyStorageSize(storage, newItemSize) {
        let sizeLeft = storage.maxSize - storage.storageSize;
        if (storage.maxSize > 0 && newItemSize > sizeLeft) {
            if (!storage.restorable) {
                return memoryFullError(storage.items);
            }
            const result = garbageCollector.gc(storage.getRemovables());
            repositorySize -= result.removedSize;
            if (result.error) {
                return result.error;
            }

            sizeLeft = storage.maxSize - storage.storageSize;
            if (newItemSize > sizeLeft) {
                return memoryFullError(storage.items);
            }
        }
    }

    function memoryFullError(collection) {
        return new Error(`Memory storage full: ${collection}`);
    }

    function getStorage(collection) {
        if (collection in storages) {
            return storages[collection];
        }
        throw new Error(`Storage does not exist: ${collection}`);
    }
}

export default StorageManager;
